(() => {
  if (customElements.get('pp-cart-diwali')) return;
  customElements.define('pp-cart-diwali', class extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.unsubscribe?.();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.form = this.querySelector('form');
      this.button = this.form?.querySelector('button');
      if (!this.button) return;
      this.label = this.button.querySelector('span');
      this.original = this.label.textContent;
      this.added = this.dataset.added === 'true';
      this.sync();
      const event = window.MinimogTheme?.pubSubEvents?.cartUpdate;
      if (event && window.MinimogEvents?.subscribe) {
        this.unsubscribe = window.MinimogEvents.subscribe(event, cart => {
          // A complete cart is required to infer absence; add responses contain only new lines.
          if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count)) return;
          this.added = cart.items.some(item => String(item.product_id) === this.dataset.productId);
          this.sync();
        });
      }
      this.form.addEventListener('submit', event => {
        if (this.pending || this.added || this.uncertain) { event.preventDefault(); return; }
        if (!this.cartTarget()) return;
        event.preventDefault();
        this.add();
      }, options);
      this.querySelector('[data-diwali-refresh]').addEventListener('click', () => this.recover(), options);
      this.cartTarget()?.addEventListener('click', event => {
        const target = this.cartTarget();
        if ((target.hasAttribute('data-pp-add-pending') || target.hasAttribute('data-pp-add-uncertain')) && event.target.closest('.sr-headless-checkout, [name="checkout"]')) {
          event.preventDefault(); event.stopImmediatePropagation();
        }
      }, { ...options, capture: true });
    }
    disconnectedCallback() { this.listeners?.abort(); this.unsubscribe?.(); }
    cartTarget() { return document.querySelector('m-cart-drawer') || document.querySelector('m-cart'); }
    sync() {
      this.querySelectorAll('[data-diwali-absent]').forEach(node => { node.hidden = this.added; });
      this.querySelector('[data-diwali-present]').hidden = !this.added;
      this.button.disabled = Boolean(this.added || this.pending || this.uncertain);
      this.button.setAttribute('aria-busy', String(Boolean(this.pending)));
      this.label.textContent = this.pending ? this.button.dataset.adding : this.original;
      this.querySelector('[data-diwali-refresh]').hidden = !this.uncertain || this.pending;
    }
    message(text) {
      const output = this.querySelector('[data-diwali-error]');
      output.textContent = text || '';
      output.hidden = !text;
    }
    async request(url, options) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        const result = await response.json();
        if (!response.ok || result.status) {
          const error = new Error(result.description || this.dataset.error);
          const status = Number(response.status || result.status);
          error.rejected = status >= 400 && status < 500 && status !== 408;
          throw error;
        }
        return result;
      } finally { clearTimeout(timer); }
    }
    sections(target) {
      return [...new Set(target.getSectionsToRender().map(region => region.section || region.id))];
    }
    render(target, result) {
      const regions = target.getSectionsToRender();
      // Validate all regions before replacing anything, so missing HTML cannot leave stale totals.
      const replacements = regions.map(region => {
        const html = result.sections?.[region.section || region.id];
        const source = typeof html === 'string' && new DOMParser().parseFromString(html, 'text/html').querySelector(region.selector);
        const destination = document.querySelector(region.selector);
        if (!source || !destination) throw new Error('Missing cart region');
        return { source, destination };
      });
      replacements.forEach(({ source, destination }) => { destination.innerHTML = source.innerHTML; });
      target.classList.remove('m-cart--empty');
      if (this.dataset.context === 'product') target.open?.(this.button);
    }
    async add() {
      const target = this.cartTarget();
      if (!target || this.pending || this.added || this.uncertain || target.ppCartBusy?.() || target.hasAttribute('data-pp-add-pending') || target.hasAttribute('data-pp-add-uncertain')) return;
      const root = window.Shopify?.routes?.root || '/';
      this.pending = true;
      this.message('');
      this.sync();
      target.setAttribute('data-pp-add-pending', '');
      target.ppSyncCart?.();
      let confirmed = false;
      try {
        const result = await this.request(root + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(this.form.elements.id.value), quantity: 1 }], sections: this.sections(target), sections_url: location.pathname }),
        });
        confirmed = true;
        document.querySelectorAll('pp-cart-diwali').forEach(widget => { widget.added = true; widget.sync(); });
        this.render(target, result);
        window.MinimogEvents?.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { ...result, source: 'main-cart-items' });
      } catch (error) {
        if (!confirmed && error.rejected) { this.message(error.message); return; }
        // Never repeat an ambiguous add; reconcile using read-only requests.
        this.uncertain = true;
        target.setAttribute('data-pp-add-uncertain', '');
        this.message(confirmed ? this.dataset.error : this.dataset.uncertain);
        await this.reconcile(target, root);
      } finally {
        this.pending = false;
        target.removeAttribute('data-pp-add-pending');
        target.ppSyncCart?.();
        this.sync();
      }
    }
    async reconcile(target, root) {
      try {
        const cart = await this.request(root + 'cart.js');
        const sections = await this.request(root + 'cart?sections=' + this.sections(target).join(','));
        this.render(target, { sections });
        this.added = cart.items.some(item => String(item.product_id) === this.dataset.productId);
        this.uncertain = false;
        target.removeAttribute('data-pp-add-uncertain');
        this.message('');
        window.MinimogEvents?.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { ...cart, source: 'main-cart-items' });
      } catch (_) { this.message(this.dataset.uncertain); }
    }
    async recover() {
      if (this.pending) return;
      const target = this.cartTarget();
      if (!target || target.hasAttribute('data-pp-add-pending') || target.hasAttribute('data-pp-pending')) return;
      this.pending = true;
      this.sync();
      target.setAttribute('data-pp-add-pending', '');
      target.ppSyncCart?.();
      await this.reconcile(target, window.Shopify?.routes?.root || '/');
      this.pending = false;
      target.removeAttribute('data-pp-add-pending');
      target.ppSyncCart?.();
      this.sync();
    }
  });
})();
